import { useQuery } from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantAnalyticsProvider, {
	type CurrencyBreakdownResponse,
	type MerchantOverview,
	type NetworkBreakdownResponse,
	type OrderStatusAnalyticsResponse,
	type VolumeAnalyticsResponse,
} from "src/providers/merchant-analytics-provider";

interface DateRangeArgs {
	startDate: string;
	endDate: string;
}

const analyticsQueries = {
	getOverview: () => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: ["analytics", "overview", merchantId ?? "no-merchant"],
			queryFn: async (): Promise<MerchantOverview> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantAnalyticsProvider.getOverview(merchantId);
			},
			enabled: Boolean(merchantId),
		});
	},

	getVolumeAnalytics: ({ startDate, endDate }: DateRangeArgs) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: [
				"analytics",
				"volume",
				merchantId ?? "no-merchant",
				startDate,
				endDate,
			],
			queryFn: async (): Promise<VolumeAnalyticsResponse> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantAnalyticsProvider.getVolumeAnalytics(
					merchantId,
					startDate,
					endDate,
				);
			},
			enabled: Boolean(merchantId),
		});
	},

	getNetworkBreakdown: ({ startDate, endDate }: DateRangeArgs) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: [
				"analytics",
				"network",
				merchantId ?? "no-merchant",
				startDate,
				endDate,
			],
			queryFn: async (): Promise<NetworkBreakdownResponse> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantAnalyticsProvider.getNetworkBreakdown(
					merchantId,
					startDate,
					endDate,
				);
			},
			enabled: Boolean(merchantId),
		});
	},

	getCurrencyBreakdown: ({ startDate, endDate }: DateRangeArgs) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: [
				"analytics",
				"currency",
				merchantId ?? "no-merchant",
				startDate,
				endDate,
			],
			queryFn: async (): Promise<CurrencyBreakdownResponse> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantAnalyticsProvider.getCurrencyBreakdown(
					merchantId,
					startDate,
					endDate,
				);
			},
			enabled: Boolean(merchantId),
		});
	},

	getOrderStatusAnalytics: ({ startDate, endDate }: DateRangeArgs) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: [
				"analytics",
				"order-status",
				merchantId ?? "no-merchant",
				startDate,
				endDate,
			],
			queryFn: async (): Promise<OrderStatusAnalyticsResponse> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantAnalyticsProvider.getOrderStatusAnalytics(
					merchantId,
					startDate,
					endDate,
				);
			},
			enabled: Boolean(merchantId),
		});
	},
};

export default analyticsQueries;
